import React, { useEffect, useState, useCallback } from "react";
import { useLocation, useHistory, Link } from "react-router-dom";
import CountUp from "react-countup";
import { apiRequest } from "../../apiServices/allApi";
import "../../../assets/css/AgentData.css";

const formatIndianCurrency = (val) => {
  const num = Number(val) || 0;
  return num.toLocaleString("en-IN");
};

const getLocalISODate = (d) => {
  const offset = d.getTimezoneOffset() * 60000;
  return new Date(d.getTime() - offset).toISOString().slice(0, 10);
};

export default function AgentData() {
  const location = useLocation();
  const history = useHistory();
  const path = location.pathname.split("/")[3];

  const [agent, setAgent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [activePreset, setActivePreset] = useState("today");

  const todayStr = getLocalISODate(new Date());
  const [startDate, setStartDate] = useState(todayStr);
  const [endDate, setEndDate] = useState(todayStr);

  const fetchAgentData = useCallback(
    async (s, e, isManualRefresh = false) => {
      if (!path) return;
      if (isManualRefresh) setRefreshing(true);
      else setLoading(true);

      const access_token = localStorage.getItem("token");
      const headers = {
        Authorization: `Bearer ${access_token}`,
      };

      try {
        const queryStart = s
          ? s.toString()
          : new Date().setHours(0, 0, 0, 0).toString();
        const queryEnd = e ? e.toString() : new Date().toString();

        const res = await apiRequest(
          `admin/admin/allData/${path}?startingfrom=${encodeURIComponent(
            queryStart,
          )}&endto=${encodeURIComponent(queryEnd)}`,
          "GET",
          null,
          headers,
        );

        if (res && res.data) {
          setAgent(res.data);
        }
      } catch (err) {
        console.error("Failed to load agent analytics:", err);
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [path],
  );

  // Apply Quick Date Range Filter
  const applyPreset = (preset) => {
    setActivePreset(preset);
    const now = new Date();
    let s, e;

    if (preset === "today") {
      s = new Date(
        now.getFullYear(),
        now.getMonth(),
        now.getDate(),
        0,
        0,
        0,
        0,
      );
      e = new Date();
      setStartDate(getLocalISODate(s));
      setEndDate(getLocalISODate(now));
    } else if (preset === "yesterday") {
      s = new Date(
        now.getFullYear(),
        now.getMonth(),
        now.getDate() - 1,
        0,
        0,
        0,
        0,
      );
      e = new Date(
        now.getFullYear(),
        now.getMonth(),
        now.getDate() - 1,
        23,
        59,
        59,
        999,
      );
      setStartDate(getLocalISODate(s));
      setEndDate(getLocalISODate(s));
    } else if (preset === "30days") {
      s = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
      s.setHours(0, 0, 0, 0);
      e = new Date();
      setStartDate(getLocalISODate(s));
      setEndDate(getLocalISODate(now));
    }

    if (s && e) {
      fetchAgentData(s, e);
    }
  };

  const handleCustomApply = (e) => {
    e.preventDefault();
    if (!startDate || !endDate) return;

    setActivePreset("custom");
    const s = new Date(startDate + "T00:00:00");
    const eDate = new Date(endDate + "T23:59:59");
    fetchAgentData(s, eDate);
  };

  const handleRefresh = () => {
    if (activePreset === "custom") {
      const s = new Date(startDate + "T00:00:00");
      const e = new Date(endDate + "T23:59:59");
      fetchAgentData(s, e, true);
    } else {
      applyPreset(activePreset);
    }
  };

  useEffect(() => {
    const startOfToday = new Date(new Date().setHours(0, 0, 0, 0));
    fetchAgentData(startOfToday, new Date());
  }, [fetchAgentData]);

  // Derived metrics
  const todayDeposit = Number(agent?.todayDeposit || 0);
  const totalDeposit = Number(agent?.totalDeposit || 0);

  const todayWithdraw = Number(agent?.todayWithdraw || 0);
  const totalWithdraw = Number(agent?.totalWithdraw || 0);

  const todayManualWithdraw = Number(agent?.todayManualWithdraw || 0);
  const totalManualWithdraw = Number(agent?.totalManualWithdraw || 0);

  const todayBonus = Number(agent?.todayBouns || 0);
  const totalBonus = Number(agent?.totalBouns || 0);

  const todayPenalty = Number(agent?.todayPenalty || 0);
  const totalPenalty = Number(agent?.totalPenalty || 0);

  const agentName = agent?.adminDetails?.Name || "Agent Analytics";
  const agentPhone = agent?.adminDetails?.Phone || "";
  const agentRole = agent?.adminDetails?.user_type || "Agent";
  const agentInitial = agentName ? agentName.charAt(0).toUpperCase() : "A";

  return (
    <div className="agent-data-page">
      {/* -------------------------------------------------------------------- */}
      {/* TOP HEADER & AGENT BANNER                                            */}
      {/* -------------------------------------------------------------------- */}
      <div className="agent-header-card">
        <div className="agent-top-bar">
          <Link to="/Alladmin" className="agent-back-btn">
            <i className="mdi mdi-arrow-left"></i>
            <span>Back to All Admins & Agents</span>
          </Link>

          <div className="agent-breadcrumb">
            <span>Admin</span>
            <i className="mdi mdi-chevron-right"></i>
            <span>Agents</span>
            <i className="mdi mdi-chevron-right"></i>
            <strong>Performance Data</strong>
          </div>
        </div>

        <div className="agent-profile-wrap">
          <div className="agent-avatar-circle">{agentInitial}</div>

          <div className="agent-profile-info">
            <h2>
              {agentName}
              <span
                className={`agent-badge-pill ${
                  agentRole === "Admin"
                    ? "badge-role-admin"
                    : "badge-role-agent"
                }`}
              >
                <i
                  className={`mdi ${
                    agentRole === "Admin"
                      ? "mdi-shield-crown"
                      : "mdi-shield-account"
                  }`}
                ></i>
                {agentRole}
              </span>
            </h2>

            <div className="agent-badges-row">
              {agentPhone && (
                <span className="agent-badge-pill badge-phone">
                  <i className="mdi mdi-phone"></i>
                  {agentPhone}
                </span>
              )}
             
              <span className="agent-badge-pill badge-phone">
                <i className="mdi mdi-clock-outline"></i>
                Period: {activePreset.toUpperCase()}
              </span>
            </div>
          </div>
        </div>

        {/* ------------------------------------------------------------------ */}
        {/* DATE FILTER TOOLBAR                                                */}
        {/* ------------------------------------------------------------------ */}
        <div className="agent-filter-toolbar">
          <div className="agent-quick-presets">
            <button
              type="button"
              className={`preset-chip-btn ${
                activePreset === "today" ? "active" : ""
              }`}
              onClick={() => applyPreset("today")}
            >
              Today
            </button>
            <button
              type="button"
              className={`preset-chip-btn ${
                activePreset === "yesterday" ? "active" : ""
              }`}
              onClick={() => applyPreset("yesterday")}
            >
              Yesterday
            </button>

            <button
              type="button"
              className={`preset-chip-btn ${
                activePreset === "30days" ? "active" : ""
              }`}
              onClick={() => applyPreset("30days")}
            >
              Last 30 Days
            </button>
          </div>

          <form onSubmit={handleCustomApply} className="agent-date-inputs">
            <div className="date-field-group">
              <label>From</label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                max={todayStr}
              />
            </div>
            <div className="date-field-group">
              <label>To</label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                max={todayStr}
              />
            </div>
            <button type="submit" className="agent-apply-btn">
              <i className="mdi mdi-filter-variant"></i>
              Filter
            </button>
            <button
              type="button"
              onClick={handleRefresh}
              className="agent-refresh-btn"
              title="Refresh Data"
              disabled={refreshing}
            >
              <i
                className={`mdi mdi-refresh ${
                  refreshing ? "mdi-spin text-primary" : ""
                }`}
              ></i>
            </button>
          </form>
        </div>
      </div>

      {/* -------------------------------------------------------------------- */}
      {/* HERO KEY PERFORMANCE INDICATORS STRIP                                */}
      {/* -------------------------------------------------------------------- */}
      <div className="agent-kpi-strip">
        <div className="kpi-stat-card">
          <div className="kpi-stat-header">
            <span className="kpi-stat-label">Total Deposits</span>
            <div className="kpi-stat-icon bg-success-light text-success">
              <i className="mdi mdi-cash-plus"></i>
            </div>
          </div>
          <h3 className="kpi-stat-value text-success">
            ₹
            {loading ? (
              <span
                className="agent-skeleton d-inline-block"
                style={{ width: 100, height: 24 }}
              ></span>
            ) : (
              <CountUp
                start={0}
                end={totalDeposit}
                duration={0.6}
                formattingFn={formatIndianCurrency}
              />
            )}
          </h3>
        </div>

        <div className="kpi-stat-card">
          <div className="kpi-stat-header">
            <span className="kpi-stat-label">Total Withdrawals</span>
            <div className="kpi-stat-icon bg-warning-light text-warning">
              <i className="mdi mdi-cash-minus"></i>
            </div>
          </div>
          <h3 className="kpi-stat-value text-warning">
            ₹
            {loading ? (
              <span
                className="agent-skeleton d-inline-block"
                style={{ width: 100, height: 24 }}
              ></span>
            ) : (
              <CountUp
                start={0}
                end={totalWithdraw}
                duration={0.6}
                formattingFn={formatIndianCurrency}
              />
            )}
          </h3>
        </div>

        <div className="kpi-stat-card">
          <div className="kpi-stat-header">
            <span className="kpi-stat-label">Total Bonus</span>
            <div className="kpi-stat-icon bg-info-light text-info">
              <i className="mdi mdi-swap-horizontal-bold"></i>
            </div>
          </div>
          <h3 className="kpi-stat-value text-dark">
            ₹
            {loading ? (
              <span
                className="agent-skeleton d-inline-block"
                style={{ width: 100, height: 24 }}
              ></span>
            ) : (
              <CountUp
                start={0}
                end={totalBonus}
                duration={0.6}
                formattingFn={formatIndianCurrency}
              />
            )}
          </h3>
        </div>

        <div className="kpi-stat-card">
          <div className="kpi-stat-header">
            <span className="kpi-stat-label">Total Penalty</span>
            <div className="kpi-stat-icon bg-info-light text-info">
              <i className="mdi mdi-swap-horizontal-bold"></i>
            </div>
          </div>
          <h3 className="kpi-stat-value text-dark">
            ₹
            {loading ? (
              <span
                className="agent-skeleton d-inline-block"
                style={{ width: 100, height: 24 }}
              ></span>
            ) : (
              <CountUp
                start={0}
                end={totalPenalty}
                duration={0.6}
                formattingFn={formatIndianCurrency}
              />
            )}
          </h3>
        </div>
      </div>

      {/* -------------------------------------------------------------------- */}
      {/* SECTION 1: DEPOSITS OVERVIEW                                         */}
      {/* -------------------------------------------------------------------- */}
      <div className="agent-section-header">
        <h4 className="agent-section-header-title text-success">
          <i className="mdi mdi-cash-plus"></i>
          Deposits Handled by Agent
        </h4>
        <span className="agent-section-tag">Inward Volume</span>
      </div>

      <div className="agent-cards-grid">
        <div className="agent-stat-card card-emerald">
          <div className="card-top-row">
            <h5 className="card-title-text">
              {" "}
              {activePreset.toUpperCase()} Deposit
            </h5>
            <div className="card-icon-bubble">
              <i className="mdi mdi-calendar-today"></i>
            </div>
          </div>
          <div className="card-value-display">
            <span className="card-currency-symbol">₹</span>
            <span className="card-count-number">
              {loading ? (
                <span
                  className="agent-skeleton d-inline-block"
                  style={{ width: 110, height: 28 }}
                ></span>
              ) : (
                <CountUp
                  start={0}
                  end={todayDeposit}
                  duration={0.5}
                  formattingFn={formatIndianCurrency}
                />
              )}
            </span>
          </div>
        </div>

        <div className="agent-stat-card card-emerald">
          <div className="card-top-row">
            <h5 className="card-title-text">Total Deposit</h5>
            <div className="card-icon-bubble">
              <i className="mdi mdi-bank-transfer-in"></i>
            </div>
          </div>
          <div className="card-value-display">
            <span className="card-currency-symbol">₹</span>
            <span className="card-count-number">
              {loading ? (
                <span
                  className="agent-skeleton d-inline-block"
                  style={{ width: 110, height: 28 }}
                ></span>
              ) : (
                <CountUp
                  start={0}
                  end={totalDeposit}
                  duration={0.5}
                  formattingFn={formatIndianCurrency}
                />
              )}
            </span>
          </div>
        </div>
      </div>

      {/* -------------------------------------------------------------------- */}
      {/* SECTION 2: WITHDRAWALS OVERVIEW                                      */}
      {/* -------------------------------------------------------------------- */}
      <div className="agent-section-header">
        <h4 className="agent-section-header-title text-warning">
          <i className="mdi mdi-cash-minus"></i>
          Withdrawals Handled by Agent
        </h4>
        <span className="agent-section-tag">Outward Payouts</span>
      </div>

      <div className="agent-cards-grid">
        <div className="agent-stat-card card-amber">
          <div className="card-top-row">
            <h5 className="card-title-text">
              {activePreset.toUpperCase()} Withdraw
            </h5>
            <div className="card-icon-bubble">
              <i className="mdi mdi-server-network"></i>
            </div>
          </div>
          <div className="card-value-display">
            <span className="card-currency-symbol">₹</span>
            <span className="card-count-number">
              {loading ? (
                <span
                  className="agent-skeleton d-inline-block"
                  style={{ width: 110, height: 28 }}
                ></span>
              ) : (
                <CountUp
                  start={0}
                  end={todayWithdraw}
                  duration={0.5}
                  formattingFn={formatIndianCurrency}
                />
              )}
            </span>
          </div>
        </div>

        <div className="agent-stat-card card-amber">
          <div className="card-top-row">
            <h5 className="card-title-text">Total Withdraw</h5>
            <div className="card-icon-bubble">
              <i className="mdi mdi-bank-transfer-out"></i>
            </div>
          </div>
          <div className="card-value-display">
            <span className="card-currency-symbol">₹</span>
            <span className="card-count-number">
              {loading ? (
                <span
                  className="agent-skeleton d-inline-block"
                  style={{ width: 110, height: 28 }}
                ></span>
              ) : (
                <CountUp
                  start={0}
                  end={totalWithdraw}
                  duration={0.5}
                  formattingFn={formatIndianCurrency}
                />
              )}
            </span>
          </div>
        </div>

        <div className="agent-stat-card card-purple">
          <div className="card-top-row">
            <h5 className="card-title-text">
              {activePreset.toUpperCase()} Manual Withdraw
            </h5>
            <div className="card-icon-bubble">
              <i className="mdi mdi-hand-coin-outline"></i>
            </div>
          </div>
          <div className="card-value-display">
            <span className="card-currency-symbol">₹</span>
            <span className="card-count-number">
              {loading ? (
                <span
                  className="agent-skeleton d-inline-block"
                  style={{ width: 110, height: 28 }}
                ></span>
              ) : (
                <CountUp
                  start={0}
                  end={todayManualWithdraw}
                  duration={0.5}
                  formattingFn={formatIndianCurrency}
                />
              )}
            </span>
          </div>
        </div>

        <div className="agent-stat-card card-purple">
          <div className="card-top-row">
            <h5 className="card-title-text">Total Manual Withdraw</h5>
            <div className="card-icon-bubble">
              <i className="mdi mdi-hand-coin"></i>
            </div>
          </div>
          <div className="card-value-display">
            <span className="card-currency-symbol">₹</span>
            <span className="card-count-number">
              {loading ? (
                <span
                  className="agent-skeleton d-inline-block"
                  style={{ width: 110, height: 28 }}
                ></span>
              ) : (
                <CountUp
                  start={0}
                  end={totalManualWithdraw}
                  duration={0.5}
                  formattingFn={formatIndianCurrency}
                />
              )}
            </span>
          </div>
        </div>
      </div>

      {/* -------------------------------------------------------------------- */}
      {/* SECTION 3: BONUSES & REWARDS                                         */}
      {/* -------------------------------------------------------------------- */}
      <div className="agent-section-header">
        <h4 className="agent-section-header-title text-info">
          <i className="mdi mdi-gift-outline"></i>
          Bonuses Credited by Agent
        </h4>
        <span className="agent-section-tag">Rewards</span>
      </div>

      <div className="agent-cards-grid">
        <div className="agent-stat-card card-cyan">
          <div className="card-top-row">
            <h5 className="card-title-text">
              {activePreset.toUpperCase()} Bonus
            </h5>
            <div className="card-icon-bubble">
              <i className="mdi mdi-gift"></i>
            </div>
          </div>
          <div className="card-value-display">
            <span className="card-currency-symbol">₹</span>
            <span className="card-count-number">
              {loading ? (
                <span
                  className="agent-skeleton d-inline-block"
                  style={{ width: 110, height: 28 }}
                ></span>
              ) : (
                <CountUp
                  start={0}
                  end={todayBonus}
                  duration={0.5}
                  formattingFn={formatIndianCurrency}
                />
              )}
            </span>
          </div>
        </div>

        <div className="agent-stat-card card-cyan">
          <div className="card-top-row">
            <h5 className="card-title-text">Total Bonus</h5>
            <div className="card-icon-bubble">
              <i className="mdi mdi-star-circle-outline"></i>
            </div>
          </div>
          <div className="card-value-display">
            <span className="card-currency-symbol">₹</span>
            <span className="card-count-number">
              {loading ? (
                <span
                  className="agent-skeleton d-inline-block"
                  style={{ width: 110, height: 28 }}
                ></span>
              ) : (
                <CountUp
                  start={0}
                  end={totalBonus}
                  duration={0.5}
                  formattingFn={formatIndianCurrency}
                />
              )}
            </span>
          </div>
        </div>
      </div>

      {/* -------------------------------------------------------------------- */}
      {/* SECTION 4: PENALTIES & DEDUCTIONS                                    */}
      {/* -------------------------------------------------------------------- */}
      <div className="agent-section-header">
        <h4 className="agent-section-header-title text-danger">
          <i className="mdi mdi-alert-circle-outline"></i>
          Penalties Charged by Agent
        </h4>
        <span className="agent-section-tag">Deductions</span>
      </div>

      <div className="agent-cards-grid">
        <div className="agent-stat-card card-rose">
          <div className="card-top-row">
            <h5 className="card-title-text">
              {activePreset.toUpperCase()} Penalty
            </h5>
            <div className="card-icon-bubble">
              <i className="mdi mdi-gavel"></i>
            </div>
          </div>
          <div className="card-value-display">
            <span className="card-currency-symbol">₹</span>
            <span className="card-count-number">
              {loading ? (
                <span
                  className="agent-skeleton d-inline-block"
                  style={{ width: 110, height: 28 }}
                ></span>
              ) : (
                <CountUp
                  start={0}
                  end={todayPenalty}
                  duration={0.5}
                  formattingFn={formatIndianCurrency}
                />
              )}
            </span>
          </div>
        </div>

        <div className="agent-stat-card card-rose">
          <div className="card-top-row">
            <h5 className="card-title-text">Total Penalty</h5>
            <div className="card-icon-bubble">
              <i className="mdi mdi-shield-alert-outline"></i>
            </div>
          </div>
          <div className="card-value-display">
            <span className="card-currency-symbol">₹</span>
            <span className="card-count-number">
              {loading ? (
                <span
                  className="agent-skeleton d-inline-block"
                  style={{ width: 110, height: 28 }}
                ></span>
              ) : (
                <CountUp
                  start={0}
                  end={totalPenalty}
                  duration={0.5}
                  formattingFn={formatIndianCurrency}
                />
              )}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
